import {
  getObjectTypeAlias,
  getObjectTypeId,
  getPropertyId,
  getObjectClassId,
  getObjectWorkflowStateId,
  getWorkflowId,
  isOwnProperty
} from "../helpers/configurationHelpers";
import {
  getObjectTypeIdsFromAliasesAsync,
  getPropertyIdsFromAliasesAsync,
  getObjectClassIdsFromAliasesAsync,
  getWorkflowStateIdsFromAliasesAsync,
  getWorkflowIdsFromAliasesAsync,
} from "../services/AliasService";
import { AxiosInstance } from "axios";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import uniq from "lodash/uniq";
import { getAllPropertyDefinitionsAsync, getPropertyDefinitionsOfObjectTypes, getObjectTypeClassesAsync, getAllObjectTypesAsync } from "../services/PropertiesService";
import {
  IConfiguration,
  IAliasConfiguration,
  IViewConfigurationItem,
  ILinkConfigurationItem,
  IDragAndDropConfigurationItem,
  IEditObjectConfiguration,
  IDefaultPropertyValue,
  IObjectTypeConfigurationItem,
  IObjectHierarchyItem,
  IObjectTypeId,
  IWorkflow,
  IFilterConfigurationItem,
  IOwnPropertyOrId,
  IEditPropertyValue,
  IWorkflowId,
  IWorkflowState,
} from "../interfaces/configuration/IConfiguration";
import {
  IExternalConfiguration,
  IExternalViewConfigurationItem,
  IExternalLinkConfigurationItem,
  IExternalDragAndDropConfigurationItem,
  IExternalEditObjectConfiguration,
  IExternalDefaultPropertyValue,
  IExternalObjectTypeConfigurationItem,
  IExternalObjectHierarchyItem,
  IExternalFilter,
  IPropertyAliasOrId,
  IExternalEditPropertyValue,
  IAliasOrId,
} from "../interfaces/configuration/IExternalConfiguration";
import { getRandomAliasMapping, getPropertyDefinitions } from "../devmode/TestData";
import { DEFAULT_FETCHED_ITEMS_COUNT_LIMIT, DEFAULT_MAX_DATERANGE_IN_YEARS, MFILES_LINK_VINCITEAM, VINCITEAM_URLSEARCHOBJECT } from "../constants/common";
import { getAllValueListDefinitionsAsync } from "../services/ValueListService";
import { getAllWorkflows, getWorkflowStates } from "../services/StateService";
import { getSessionAsync, getSessionVaultAsync } from "../services/UserService";
import { IBaseConfiguration } from "../interfaces/configuration/IBaseConfiguration";
import { IMfilesObjectClass } from "../interfaces/mfiles/IMfilesObjectClass";
import { BuiltInPropertyDef, isBuildinProperty, stringToBuiltinProperty } from "../constants/builtinProperties";
import { filterOperatorToComparisonCharacter, resolveMFilesApiBaseUrl, resolveVaultGuid } from "../helpers/urlHelper";
import { FilterOperator } from "../constants/filterOperators";
import { IMFilesObjectType } from "../interfaces/mfiles/IMFilesObjectType";
import { parseDate } from "src/gantt/helpers/DateHelpers";
import ganttDB from "../storage/database";
import IMFilesPropertyDefinition from "../interfaces/mfiles/IMFilesPropertyDefinition";
import IMfilesValueListDefinition from "../interfaces/mfiles/IMFilesValueListDefinition";
import { isExpression, removeExpressionBrackets, parseExpression } from "../helpers/expressionHelper";

export const getConfigAsync = async (
  httpClient: AxiosInstance,
  externalConfig: IExternalConfiguration,
  baseConfiguration: IBaseConfiguration
): Promise<IConfiguration> => {
  const allObjectTypeAliases = getObjectTypeAliases(externalConfig);
  const allObjectClassAliases = getObjectClassAliases(externalConfig);
  const allWorkflowStateAliases = getWorkflowStateAliases(externalConfig);
  const allWorkflowAliases = getWorkflowAliases(externalConfig);
  const allPropertyAliases = getPropertyAliases(externalConfig);

  throwErrorIfEmptyAliasesFound(allObjectTypeAliases, "Object Type");
  throwErrorIfEmptyAliasesFound(allObjectClassAliases, "Object Class");
  throwErrorIfEmptyAliasesFound(allWorkflowStateAliases, "Workflow State");
  throwErrorIfEmptyAliasesFound(allWorkflowAliases, "Workflow");
  throwErrorIfEmptyAliasesFound(allPropertyAliases, "Property");

  if (baseConfiguration.devModeOn) {
    const aliasConfig: IAliasConfiguration = {
      objectTypeAliases: getRandomAliasMapping(allObjectTypeAliases),
      objectClassAliases: getRandomAliasMapping(allObjectClassAliases),
      objectWorkflowStateAliases: getRandomAliasMapping(allWorkflowStateAliases),
      propertyAliases: getRandomAliasMapping(allPropertyAliases),
      workflowAliases: getRandomAliasMapping(allWorkflowAliases),
    };

    const propertyDefinitions = new Map(getPropertyDefinitions().map((def) => [def.ID, def]));
    const viewConfigurations = externalConfig.viewConfigurations.map((externalViewConfig) =>
      parseViewConfig(aliasConfig, externalViewConfig)
    );
    const objectTypeConfigurations = externalConfig.objectTypeConfigurations.map((externalObjectTypeConfig) =>
      parseObjectTypeConfig(aliasConfig, externalObjectTypeConfig, [])
    );
    const objectTypeConfigurationsMapping = new Map(objectTypeConfigurations.map((o) => [o.objectTypeId, o]));
    const allObjectTypeIds = Array.from(objectTypeConfigurations.keys());

    return {
      vaultGuid: resolveVaultGuid(baseConfiguration, externalConfig),
      mfilesApiBaseUrl: resolveMFilesApiBaseUrl(baseConfiguration, externalConfig),
      mfilesLinkType: externalConfig.mfilesLinkType,
      objectTypeConfigurations: objectTypeConfigurationsMapping,
      viewConfigurations: viewConfigurations,
      aliasConfiguration: aliasConfig,
      fetchedItemsCountLimit: externalConfig.fetchedItemsCountLimit || DEFAULT_FETCHED_ITEMS_COUNT_LIMIT,
      linkConfigurations: externalConfig.linkConfigurations.map((externalLinkConfig) => parseLinkConfig(aliasConfig, externalLinkConfig)),
      propertyDefinitions: propertyDefinitions,
      dragndropConfigurations: externalConfig.dragndropConfigurations.map((dndCfg) => parseDragNDropConfig(aliasConfig, dndCfg)),
      valueListDefinitions: new Map(),
      associatedPropertyDefinitionsOfObjectTypes: new Map(allObjectTypeIds.map((otId) => [otId, [...propertyDefinitions.keys()]])),
      workflows: new Map(),
      userId: -1,
      userName: "user",
      vaultName: "vault",
      isAdmin: false,
      baseConfiguration: baseConfiguration,
      maxDateRangeInYears: externalConfig.maxDateRangeInYears ?? DEFAULT_MAX_DATERANGE_IN_YEARS,
      valueListItems: [],
      classes: new Map(),
    };
  } else {
    //Properties takes longest to finish, so start it first
    const propertyDefinitionsPromise = getCachedPropertyDefinitionsAsync(httpClient);
    const objectTypesPromise = getCachedObjectTypesAsync(httpClient);
    //Rest of the metadata can be loaded in parallel
    const [
      objectTypeAliasesMapping,
      objectClassAliasesMapping,
      workflowStateAliasesMapping,
      propertyAliasesMapping,
      workflowAliasesMapping,
      valueListDefinitions,
      workflows,
      sessionInfo,
      vaultInfo,
    ] = await Promise.all([
      getObjectTypeAliasesToIdsMappingAsync(httpClient, allObjectTypeAliases),
      getObjectClassAliasesToIdsMappingAsync(httpClient, allObjectClassAliases),
      getWorkflowStateAliasesToIdsMappingAsync(httpClient, allWorkflowStateAliases),
      getPropertyAliasesToIdsMappingAsync(httpClient, allPropertyAliases),
      getWorkflowAliasesToIdsMappingAsync(httpClient, allWorkflowAliases),
      getCachedValueListDefinitionsAsync(httpClient),
      getCachedWorkflows(httpClient),
      getSessionAsync(httpClient),
      getSessionVaultAsync(httpClient),
    ]);

    const aliasConfig: IAliasConfiguration = {
      objectTypeAliases: validateAliasMapping(objectTypeAliasesMapping),
      objectClassAliases: validateAliasMapping(objectClassAliasesMapping),
      propertyAliases: validateAliasMapping(propertyAliasesMapping),
      objectWorkflowStateAliases: validateAliasMapping(workflowStateAliasesMapping),
      workflowAliases: validateAliasMapping(workflowAliasesMapping),
    };

    const objectTypes = await objectTypesPromise;

    const objectTypeConfigurations = externalConfig.objectTypeConfigurations.map((externalObjectTypeConfig) =>
      parseObjectTypeConfig(aliasConfig, externalObjectTypeConfig, objectTypes)
    );
    const objectTypeConfigurationsMapping = new Map(objectTypeConfigurations.map((o) => [o.objectTypeId, o]));
    const allObjectTypeIds = Array.from(objectTypeConfigurationsMapping.keys());
    
    //Get classes for configured object types
    const relevantObjectClasses = await Promise.all(
      allObjectTypeIds.map(async (objectType) => {
        return getCachedObjectTypeClassesAsync(httpClient, objectType);
      })
    );

    let allObjectClasses: IMfilesObjectClass[] = [];
    for(const relevantObjectClass of relevantObjectClasses) {
      allObjectClasses = allObjectClasses.concat(relevantObjectClass);
    }

    const relevantWorkflows = deduceRelevantWorkflows(objectTypeConfigurations, workflows, allObjectClasses);

    //Load workflow states
    const relevantWorkflowsWithStates = await Promise.all(
      relevantWorkflows.map(async (workflow) => {
        const states = await getCachedWorkflowStates(httpClient, workflow.ID);
        return { ...workflow, States: states.map((s) => ({ ...s, WorkflowName: workflow.Name })) };
      })
    );

    //Wait for property definitions to finish
    const propertyDefinitions = await propertyDefinitionsPromise;

    return {
      vaultGuid: resolveVaultGuid(baseConfiguration, externalConfig),
      mfilesApiBaseUrl: resolveMFilesApiBaseUrl(baseConfiguration, externalConfig),
      mfilesLinkType: externalConfig.mfilesLinkType,
      objectTypeConfigurations: objectTypeConfigurationsMapping,
      viewConfigurations: externalConfig.viewConfigurations.map((externalViewConfig) => parseViewConfig(aliasConfig, externalViewConfig)),
      aliasConfiguration: aliasConfig,
      fetchedItemsCountLimit: externalConfig.fetchedItemsCountLimit || DEFAULT_FETCHED_ITEMS_COUNT_LIMIT,
      linkConfigurations: externalConfig.linkConfigurations.map((externalLinkConfig) => parseLinkConfig(aliasConfig, externalLinkConfig)),
      propertyDefinitions: new Map(propertyDefinitions.map((propertyDefinition) => [propertyDefinition.ID, propertyDefinition])),
      dragndropConfigurations: externalConfig.dragndropConfigurations.map((dndCfg) => parseDragNDropConfig(aliasConfig, dndCfg)),
      valueListDefinitions: new Map(valueListDefinitions.map((def) => [def.ID, def])),
      associatedPropertyDefinitionsOfObjectTypes: getPropertyDefinitionsOfObjectTypes(allObjectClasses, allObjectTypeIds),
      workflows: new Map(relevantWorkflowsWithStates.map((w) => [w.ID, w])),
      userId: sessionInfo.UserID,
      userName: sessionInfo.FullName ? sessionInfo.FullName : sessionInfo.AccountName,
      vaultName: vaultInfo.Name,
      isAdmin: sessionInfo.IsAdminUser || sessionInfo.HasFullControlOfVault,
      baseConfiguration: baseConfiguration,
      maxDateRangeInYears: externalConfig.maxDateRangeInYears ?? DEFAULT_MAX_DATERANGE_IN_YEARS,
      valueListItems: [],
      classes: new Map(allObjectClasses.map(i => [i.ID, i])),
    };
  }
};

// Object type aliases are resolved to object type ids, which will be used within gantt.
// Ids vary from vault to vault, but every object type, property, etc. has one.
// Aliases persist from vault to vault, but not everything has an alias. Or at least won't
// hold for most clients.
const getObjectTypeAliases = (config: IExternalConfiguration): string[] => {
  return uniq([
    ...config.objectTypeConfigurations.flatMap((objectTypeConfiguration) => [
      objectTypeConfiguration.aliasOrId,
      ...(objectTypeConfiguration.editConfig?.childObjectTypesAndProperties?.flatMap(child => [child[0]]) || [])
    ]),
    ...(config.viewConfigurations.flatMap((viewConfiguration) => [
      ...(viewConfiguration.objectHierarchy.map((h) => h.aliasOrId)),
      ...(viewConfiguration.objectHierarchy.flatMap((objHier) => [
        ...(objHier.childObjectTypes?.map(c => c[0]) ?? [])
      ])),
    ])),
    ...(config.linkConfigurations.map(l => l.from)),
    ...(config.linkConfigurations.map(l => l.to)),
    ...(config.dragndropConfigurations.map(d => d.sourceObjectType)),
    ...(config.dragndropConfigurations.map(d => d.targetObjectType))
  ]).filter(isString);
};

// Property aliases are resolved to property ids, which will be used within gantt.
// Ids vary from vault to vault, but every object type, property, etc. has one.
// Aliases persist from vault to vault, but not everything has an alias. Or at least won't
// hold for most clients.
const getPropertyAliases = (config: IExternalConfiguration): string[] => {
  return uniq([
    config.mfilesLinkType === MFILES_LINK_VINCITEAM ? VINCITEAM_URLSEARCHOBJECT : null,
    ...(config.objectTypeConfigurations.flatMap((objectTypeConfiguration) => [
      ...(objectTypeConfiguration.dateProperties || []),
      ...(objectTypeConfiguration.timeProperties || []),
      ...(objectTypeConfiguration.editConfig?.defaultPropertyValues?.map((d) => d.propertyAliasOrId) || []),
      ...(objectTypeConfiguration.editConfig?.editProperties?.filter((f) => !isOwnProperty(f.propertyAliasOrId.toString()) && !isBuildinProperty(f.propertyAliasOrId.toString())).map((f) => f.propertyAliasOrId.toString()) || []),
      ...(objectTypeConfiguration.editConfig?.childObjectTypesAndProperties?.flatMap(child => [child[1]]) || []),
      ...(objectTypeConfiguration.filters?.filter((f) => !isBuildinProperty(f.propertyAliasOrId.toString())).map((f) => f.propertyAliasOrId.toString()) || []),
      ...(objectTypeConfiguration.tooltip?.filter((f) => !isOwnProperty(f.toString()) && !isBuildinProperty(f.toString())).map((f) => f.toString()) || []),
      ...(objectTypeConfiguration.gridColumns?.filter((f) => !isOwnProperty(f.toString()) && !isBuildinProperty(f.toString())).map((f) => f.toString()) || []),
      objectTypeConfiguration.editConfig?.namePropertyAliasOrId,
      objectTypeConfiguration.editConfig?.resourcePropertyAliasOrId,
      objectTypeConfiguration.durationProperty,
      !isBuildinProperty(objectTypeConfiguration.gridTextProperty?.toString() ?? '') ? objectTypeConfiguration.gridTextProperty : null,
    ])),
    ...(config.viewConfigurations.flatMap((viewConfig) =>
      viewConfig.objectHierarchy.flatMap((objectHierarchyItem) => [
        ...(objectHierarchyItem.childObjectTypes?.map(c => c[1]) ?? []),
      ])
    )),
    ...(config.linkConfigurations.map((externalLinkConfig) => externalLinkConfig.propertyAliasOrId)),
    ...(config.dragndropConfigurations.flatMap((dndConfig) => [
      dndConfig.sourcePropertyToUpdate,
      dndConfig.targetPropertyToUpdate
    ])),
  ]).filter(isString);
};

const getObjectClassAliases = (config: IExternalConfiguration): string[] => {
  return config.objectTypeConfigurations.flatMap((objectTypeConfiguration) => [
    ...(objectTypeConfiguration.filters?.filter((f) => stringToBuiltinProperty(f.propertyAliasOrId.toString()) === BuiltInPropertyDef.Class).map((f) => f.value) || []),
    objectTypeConfiguration.editConfig?.defaultObjectClassAliasOrId?.toString(),
  ]).filter(isString);
};

const getWorkflowStateAliases = (config: IExternalConfiguration): string[] => {
  return config.objectTypeConfigurations.flatMap((objectTypeConfiguration) => [
    ...(objectTypeConfiguration.filters?.filter((f) => stringToBuiltinProperty(f.propertyAliasOrId.toString()) === BuiltInPropertyDef.State).map((f) => f.value) || []),
    objectTypeConfiguration.editConfig?.defaultWorkflowStateAliasOrId?.toString(),
  ]).filter(isString);
};

const getWorkflowAliases = (config: IExternalConfiguration): string[] => {
  return config.objectTypeConfigurations
    .filter((objectTypeConfig) => objectTypeConfig.editConfig != null)
    .map((objectTypeConfig) => objectTypeConfig.editConfig?.workflowAliasOrId)
    .filter(isString);
};

const parseViewConfig = (aliasConfig: IAliasConfiguration, externalViewconfig: IExternalViewConfigurationItem): IViewConfigurationItem => {
  return {
    canMoveTasks: externalViewconfig.canMoveTasks,
    viewType: externalViewconfig.viewType,
    displayName: externalViewconfig.displayName,
    translationKey: parseViewTranslationKey(aliasConfig, externalViewconfig),
    objectHierarchy: new Map(
      externalViewconfig.objectHierarchy.map((externalObjectHierarchyItem) => {
        const objectHierarchyItem = parseObjectHierarchyItem(aliasConfig, externalObjectHierarchyItem);
        return [objectHierarchyItem.id, objectHierarchyItem];
      })
    ),
  };
};

const parseViewTranslationKey = (aliasConfig: IAliasConfiguration, externalViewconfig: IExternalViewConfigurationItem): string => {
  if(externalViewconfig.translationKey) {
    return externalViewconfig.translationKey;
  }
  if(externalViewconfig.objectHierarchy.length) {
    const objectHierarchyItem = parseObjectHierarchyItem(aliasConfig, externalViewconfig.objectHierarchy[0]);
    if(objectHierarchyItem?.id) {
      return `ObjectType_${objectHierarchyItem.id}_0_NamePlural`;
    }
  }
  return null;
}

const parseObjectHierarchyItem = (
  aliasConfig: IAliasConfiguration,
  externalObjectHierarchyItem: IExternalObjectHierarchyItem
): IObjectHierarchyItem => {
  return {
    id: getObjectTypeId(aliasConfig, externalObjectHierarchyItem.aliasOrId),
    childObjectTypes: externalObjectHierarchyItem.childObjectTypes.map(([childObjectTypeAliasOrId, parentPropertyAliasOrId]) => [
      getObjectTypeId(aliasConfig, childObjectTypeAliasOrId),
      getPropertyId(aliasConfig, parentPropertyAliasOrId),
    ]),
  };
};

const parseLinkConfig = (
  aliasConfig: IAliasConfiguration,
  externalSingleLinkConfig: IExternalLinkConfigurationItem
): ILinkConfigurationItem => {
  return {
    from: getObjectTypeId(aliasConfig, externalSingleLinkConfig.from),
    to: getObjectTypeId(aliasConfig, externalSingleLinkConfig.to),
    property: getPropertyId(aliasConfig, externalSingleLinkConfig.propertyAliasOrId),
  };
};

const parseDragNDropConfig = (
  aliasConfig: IAliasConfiguration,
  externalSingleDragAndDropConfig: IExternalDragAndDropConfigurationItem
): IDragAndDropConfigurationItem => ({
  sourceObjectType: getObjectTypeId(aliasConfig, externalSingleDragAndDropConfig.sourceObjectType),
  targetObjectType: getObjectTypeId(aliasConfig, externalSingleDragAndDropConfig.targetObjectType),
  sourcePropertyToUpdate: externalSingleDragAndDropConfig.sourcePropertyToUpdate
    ? getPropertyId(aliasConfig, externalSingleDragAndDropConfig.sourcePropertyToUpdate)
    : null,
  targetPropertyToUpdate: externalSingleDragAndDropConfig.targetPropertyToUpdate
    ? getPropertyId(aliasConfig, externalSingleDragAndDropConfig.targetPropertyToUpdate)
    : null,
});

const parseObjectTypeConfig = (
  aliasConfig: IAliasConfiguration,
  externalObjectTypeConfiguration: IExternalObjectTypeConfigurationItem,
  objectTypes: IMFilesObjectType[]
): IObjectTypeConfigurationItem => {
  const alias = isString(externalObjectTypeConfiguration.aliasOrId)
    ? externalObjectTypeConfiguration.aliasOrId
    : getObjectTypeAlias(aliasConfig, externalObjectTypeConfiguration.aliasOrId);

  const objectTypeId = isNumber(externalObjectTypeConfiguration.aliasOrId)
    ? externalObjectTypeConfiguration.aliasOrId
    : getObjectTypeId(aliasConfig, externalObjectTypeConfiguration.aliasOrId);

  const objectType = objectTypes.find(i => i.ID === objectTypeId);

  let dateProperties = null;
  if (externalObjectTypeConfiguration.dateProperties) {
    const [startDateProperty, endDateProperty] = externalObjectTypeConfiguration.dateProperties;
    dateProperties = [getPropertyId(aliasConfig, startDateProperty), getPropertyId(aliasConfig, endDateProperty)];
  }

  let timeProperties = null;
  if (externalObjectTypeConfiguration.timeProperties) {
    const [startTimeProperty, endTimeProperty] = externalObjectTypeConfiguration.timeProperties;
    timeProperties = [getPropertyId(aliasConfig, startTimeProperty), getPropertyId(aliasConfig, endTimeProperty)];
  }

  let durationProperty: number = null;
  if(externalObjectTypeConfiguration.durationProperty) {
    durationProperty = getPropertyId(aliasConfig, externalObjectTypeConfiguration.durationProperty);
  }

  let gridTextProperty: number = null;
  if(externalObjectTypeConfiguration.gridTextProperty) {
    gridTextProperty = getPropertyId(aliasConfig, externalObjectTypeConfiguration.gridTextProperty);
  }

  let timeDefaults = null;
  if(externalObjectTypeConfiguration.timeDefaults) {
    const defaultTimeFormat = "HH:mm:ss";
    const [defaultStartTime, defaultEndTime] = externalObjectTypeConfiguration.timeDefaults;
    if(!defaultStartTime) {
      throw new Error("Default StartTime is not set");
    }
    if(!defaultEndTime) {
      throw new Error("Default EndTime is not set");
    }
    timeDefaults = [parseDate(defaultStartTime, defaultTimeFormat), parseDate(defaultEndTime, defaultTimeFormat)];
  }

  return {
    alias: alias,
    objectTypeId: objectTypeId,
    name: objectType?.Name,
    namePlural: objectType?.NamePlural,
    dateProperties: dateProperties,
    timeProperties: timeProperties,
    timeDefaults: timeDefaults,
    durationProperty: durationProperty,
    durationUnit: externalObjectTypeConfiguration.durationUnit,
    gridTextProperty: gridTextProperty,
    editConfig: parseEditObjectConfiguration(aliasConfig, externalObjectTypeConfiguration.editConfig, objectTypeId),
    mayEdit: externalObjectTypeConfiguration.mayEdit,
    fetchAlways: externalObjectTypeConfiguration.fetchAlways,
    fetchRelated: externalObjectTypeConfiguration.fetchRelated,
    fetchEmptyDates: externalObjectTypeConfiguration.fetchEmptyDates,
    filters: parseFilters(aliasConfig, externalObjectTypeConfiguration.filters),
    tooltip: parseOwnPropertyOrIdArray(aliasConfig, externalObjectTypeConfiguration.tooltip),
    gridColumns: parseOwnPropertyOrIdArray(aliasConfig, externalObjectTypeConfiguration.gridColumns),
    quickSearch: parseOwnPropertyOrIdArray(aliasConfig, externalObjectTypeConfiguration.quickSearch),
  };
};

const parseEditObjectConfiguration = (
  aliasConfig: IAliasConfiguration,
  editObjectConfiguration: IExternalEditObjectConfiguration,
  objectTypeId: IObjectTypeId
): IEditObjectConfiguration => {
  if (editObjectConfiguration == null) {
    return null;
  }

  return {
    objectTypeId: objectTypeId,
    defaultObjectClassId: getObjectClassId(aliasConfig, editObjectConfiguration.defaultObjectClassAliasOrId),
    defaultWorkflowStateId: getObjectWorkflowStateId(aliasConfig, editObjectConfiguration.defaultWorkflowStateAliasOrId),
    workflowId: getWorkflowId(aliasConfig, editObjectConfiguration.workflowAliasOrId),
    nameProperty: getPropertyId(aliasConfig, editObjectConfiguration.namePropertyAliasOrId),
    childObjectTypesAndProperties: editObjectConfiguration.childObjectTypesAndProperties?.map(
      ([childObjectTypeAliasOrId, childLookupPropertyAliasOrId]) => [
        getObjectTypeId(aliasConfig, childObjectTypeAliasOrId),
        getPropertyId(aliasConfig, childLookupPropertyAliasOrId),
      ]
    ) ?? [],
    resourcePropertyId: getPropertyId(aliasConfig, editObjectConfiguration.resourcePropertyAliasOrId),
    defaultPropertyValues: editObjectConfiguration.defaultPropertyValues?.map((d) => parseDefaultPropertyValues(aliasConfig, d)) ?? [],
    editProperties: editObjectConfiguration.editProperties?.map((e) => parseEditPropertyValues(aliasConfig, e)) ?? [],
  };
};

const parseDefaultPropertyValues = (
  aliasConfig: IAliasConfiguration,
  defaultPropertyValue: IExternalDefaultPropertyValue
): IDefaultPropertyValue => {
  return {
    propertyId: getPropertyId(aliasConfig, defaultPropertyValue.propertyAliasOrId),
    defaultValue: defaultPropertyValue.defaultValue,
    useDateTimeNow: defaultPropertyValue.useDateTimeNow,
  };
};

const parseBuiltinPropertyId = (propertyAliasOrId: IAliasOrId, aliasConfig: IAliasConfiguration) => {
  if(isBuildinProperty(propertyAliasOrId.toString())) {
    return stringToBuiltinProperty(propertyAliasOrId.toString());
  }
  return getPropertyId(aliasConfig, propertyAliasOrId);
}

const parsePropertyId = (propertyAliasOrId: IAliasOrId, aliasConfig: IAliasConfiguration): string | number => {
  if(isOwnProperty(propertyAliasOrId.toString())) {
    return propertyAliasOrId.toString();
  }
  return parseBuiltinPropertyId(propertyAliasOrId, aliasConfig);
}

const parseEditPropertyValues = (
  aliasConfig: IAliasConfiguration,
  editPropertyValue: IExternalEditPropertyValue
): IEditPropertyValue => {
  const propertyId = parsePropertyId(editPropertyValue.propertyAliasOrId, aliasConfig);
  return {
    propertyIdOrName: propertyId,
    readOnly: editPropertyValue.readOnly ?? false,
    insertOnly: editPropertyValue.insertOnly ?? false,
    updateOnly: editPropertyValue.updateOnly ?? false,
    mandatory: editPropertyValue.mandatory ?? false,
  };
};

const parseExternalFilter = (externalFilter: IExternalFilter, aliasConfig: IAliasConfiguration): IFilterConfigurationItem => {
  const propertyId = parseBuiltinPropertyId(externalFilter.propertyAliasOrId, aliasConfig);
  let operator = FilterOperator.Equal;
  for(const filterOperator of filterOperatorToComparisonCharacter.keys()) {
    if(filterOperatorToComparisonCharacter.get(filterOperator) === externalFilter.operator) {
      operator = filterOperator;
    }
  }
  let value = externalFilter.value;
  if(value === undefined && operator !== FilterOperator.EmptyOrNull && operator !== FilterOperator.NotEmptyOrNull) {
    throw new Error("Filter value is missing");
  }
  if(isExpression(value)) {
    value = removeExpressionBrackets(value);
    value = parseExpression(value) as string;
  }
  if(propertyId === BuiltInPropertyDef.State) {
    value = getObjectWorkflowStateId(aliasConfig, value).toString();
  }
  if(propertyId === BuiltInPropertyDef.Class) {
    value = getObjectClassId(aliasConfig, value).toString();
  }
  return {
    property: propertyId,
    operator: operator,
    value: value
  };
}

const parseFilters = (
  aliasConfig: IAliasConfiguration,
  externalFilters: IExternalFilter[]
): IFilterConfigurationItem[] => {
  const filters: IFilterConfigurationItem[] = [];
  if(externalFilters) {
    for(const externalFilter of externalFilters) {
      filters.push(parseExternalFilter(externalFilter, aliasConfig));
    }
  }
  return filters;
};

const parseOwnPropertyOrIdArray = (
  aliasConfig: IAliasConfiguration,
  externalArray: IPropertyAliasOrId[]
): IOwnPropertyOrId[] => {
  const propertyArray: IOwnPropertyOrId[] = [];
  if(externalArray) {
    for(const propertyAliasOrId of externalArray) {
      const propertyId = parsePropertyId(propertyAliasOrId, aliasConfig);
      propertyArray.push(propertyId);
    }
  }
  return propertyArray;
};

const getAliasesToIdsMappingsAsync = async (httpClient: AxiosInstance, aliases: Array<string>, aliasType: string, getFunc: (httpClient: AxiosInstance, aliases: Array<string>) => Promise<unknown> ): Promise<Map<string, number>> => {
  const map = new Map<string,number>();
  const nonCachedAliases: string[] = [];
  for(const alias of aliases) {
    const cachedAlias = await ganttDB.aliases.get(`${aliasType}:${alias}`);
    if(cachedAlias) {
      map.set(cachedAlias.name, cachedAlias.id);
    } else {
      nonCachedAliases.push(alias);
    }
  }
  if(nonCachedAliases.length === 0) {
    return map;
  }

  for(const [key, value] of Object.entries(await getFunc(httpClient, nonCachedAliases))) {
    map.set(key,value);
    ganttDB.aliases.put({
      name: key,
      id: value
    }, `${aliasType}:${key}`);
  }
  return map;
}

// In M-Files property defitions always have an id which is used in eg. REST API calls.
// Properties can also be identified using human friendly alias strings, eg. "PD.JD.WorkOrder"
// This function gets the mapping between property defition aliases and ids via REST.
const getPropertyAliasesToIdsMappingAsync = async (httpClient: AxiosInstance, aliases: Array<string>): Promise<Map<string, number>> => {
  return getAliasesToIdsMappingsAsync(httpClient, aliases, "PD", getPropertyIdsFromAliasesAsync);
};

// In M-Files object types always have an id which is used in eg. REST API calls.
// Object types can also be identified using human friendly alias strings, eg. "OT.JD.WorkOrder"
// This function gets the mapping between object type aliases and ids via REST.
const getObjectTypeAliasesToIdsMappingAsync = async (httpClient: AxiosInstance, aliases: string[]): Promise<Map<string, number>> => {
  return getAliasesToIdsMappingsAsync(httpClient, aliases, "OT", getObjectTypeIdsFromAliasesAsync);
};

const getObjectClassAliasesToIdsMappingAsync = async (httpClient: AxiosInstance, aliases: string[]): Promise<Map<string, number>> => {
  return getAliasesToIdsMappingsAsync(httpClient, aliases, "CL", getObjectClassIdsFromAliasesAsync);
};

const getWorkflowStateAliasesToIdsMappingAsync = async (httpClient: AxiosInstance, aliases: string[]): Promise<Map<string, number>> => {
  return getAliasesToIdsMappingsAsync(httpClient, aliases, "ST", getWorkflowStateIdsFromAliasesAsync);
};

const getWorkflowAliasesToIdsMappingAsync = async (httpClient: AxiosInstance, aliases: string[]): Promise<Map<string, number>> => {
  return getAliasesToIdsMappingsAsync(httpClient, aliases, "WF", getWorkflowIdsFromAliasesAsync);
};

const validateAliasMapping = (mapping: Map<string, number>): Map<string, number> => {
  const mappingsNotFound: Array<string> = Array.from(mapping.entries())
    .filter(([_, id]) => id < 0)
    .map(([alias, _]) => alias);

  if (mappingsNotFound.length > 0) {
    throw new Error(`Configuration error: couldn't find mappings from M-Files for: ${mappingsNotFound.join(", ")}`);
  }

  return mapping;
};

const throwErrorIfEmptyAliasesFound = (values: string[], valueTypeName: string): void => {
  if(values?.includes("")) {
    throw new Error(`Empty ${valueTypeName} alias is not allowed`);
  }
}

// A relevant workflow is either:
// 1) Allocated to a certain object class
// or
// 2) Specified as to be used in editConfig of object type
const deduceRelevantWorkflows = (
  objectTypeConfigurations: IObjectTypeConfigurationItem[],
  workflows: IWorkflow[],
  relevantObjectClasses: IMfilesObjectClass[]
): IWorkflow[] => {
  const relevantObjectClassIds = relevantObjectClasses.map((c) => c.ID);
  const relevantWorkFlowIds = relevantObjectClasses.map((c) => c.Workflow);
  const configuredWorkflowsIds = objectTypeConfigurations.map((otc) => otc.editConfig?.workflowId).filter((id) => id != null);
  return workflows.filter((w) => relevantWorkFlowIds.includes(w.ID) || relevantObjectClassIds.includes(w.ObjectClass) || configuredWorkflowsIds.includes(w.ID));
};

const getCachedPropertyDefinitionsAsync = async (httpClient: AxiosInstance): Promise<Array<IMFilesPropertyDefinition>> => {
  let propertyDefinitions = await ganttDB.propertyDefinitions.toArray();
  if(propertyDefinitions?.length > 0) {
    return propertyDefinitions;
  }
  propertyDefinitions = await getAllPropertyDefinitionsAsync(httpClient);
  ganttDB.propertyDefinitions.bulkPut(propertyDefinitions);
  return propertyDefinitions;
}

const getCachedObjectTypesAsync = async (httpClient: AxiosInstance): Promise<Array<IMFilesObjectType>> => {
  let objectTypes = await ganttDB.objectTypes.toArray();
  if(objectTypes?.length > 0) {
    return objectTypes;
  }
  objectTypes = await getAllObjectTypesAsync(httpClient);
  ganttDB.objectTypes.bulkPut(objectTypes);
  return objectTypes;
}

const getCachedValueListDefinitionsAsync = async (httpClient: AxiosInstance): Promise<IMfilesValueListDefinition[]> => {
  let valueLists = await ganttDB.valueLists.toArray();
  if(valueLists?.length > 0) {
    return valueLists;
  }
  valueLists = await getAllValueListDefinitionsAsync(httpClient);
  ganttDB.valueLists.bulkPut(valueLists);
  return valueLists;
}

const getCachedWorkflows = async (httpClient: AxiosInstance): Promise<IWorkflow[]> => {
  let workflows = await ganttDB.workflows.toArray();
  if(workflows?.length > 0) {
    return workflows;
  }
  workflows = await getAllWorkflows(httpClient);
  ganttDB.workflows.bulkPut(workflows);
  return workflows;
}

const getCachedObjectTypeClassesAsync = async (httpClient: AxiosInstance, objectType: number): Promise<Array<IMfilesObjectClass>> => {
  let objectClasses = await ganttDB.objectTypeClasses.get(objectType);
  if(objectClasses?.length > 0) {
    return objectClasses;
  }
  objectClasses = await getObjectTypeClassesAsync(httpClient, objectType);
  ganttDB.objectTypeClasses.put(objectClasses, objectType);
  return objectClasses;
}

const getCachedWorkflowStates = async (httpClient: AxiosInstance, workflowId: IWorkflowId): Promise<IWorkflowState[]> => {
  let workflowStates = await ganttDB.workflowStates.get(workflowId);
  if(workflowStates?.length > 0) {
    return workflowStates;
  }
  workflowStates = await getWorkflowStates(httpClient, workflowId);
  ganttDB.workflowStates.put(workflowStates, workflowId);
  return workflowStates;
}